import React, {PropsWithChildren, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePresentationParams} from '../../../hooks/usePresentationParams';
import {useTabLock} from '../../../hooks/useTabLock';
import {ProtectionType} from '../../../incase-domain/presentationProtection';
import {setRoomInfoModalAction, setRoomProtectionAction} from '../../../store/actions/roomProtection.actions';
import {findRoomByIdAction} from '../../../store/actions/roomRunFile.actions';
import {loggedInUserSelector} from '../../../store/selectors/auth.selector';
import {roomInfoModalSelector} from '../../../store/selectors/roomProtection.selector';
import {roomSelector} from '../../../store/selectors/roomRunFile.selectors';
import {hasCommonElements} from '../../../util/array';
import AdminCredentialsModal from './lobby/modals/AdminCredentialsModal';
import RoomInfoModal from './lobby/modals/RoomInfoModal';
import UserCredentialsModal from './lobby/modals/UserCredentialsModal';
import {useFetchRoomProtection} from './queries';

function RoomLoaderWrapper({children}: PropsWithChildren<unknown>): JSX.Element {
    const dispatch = useDispatch();
    const {roomId, params, presenterCode} = usePresentationParams();

    const infoModal = useSelector(roomInfoModalSelector);
    const loggedInUser = useSelector(loggedInUserSelector);

    const {data: protection, isSuccess} = useFetchRoomProtection({roomId: roomId || ''});
    const protectionTypes = protection?.protectionTypes;

    const room = useSelector(roomSelector);
    const {isTaken: isRoomTaken} = useTabLock('room', roomId);

    useEffect(() => {
        if (isRoomTaken === true) {
            dispatch(setRoomInfoModalAction(true, 'lobby.modals.roomAlreadyOpen', 'lobby.modals.roomAlreadyOpenDesc'));
        } else if (isRoomTaken === false) {
            dispatch(setRoomInfoModalAction(false, '', ''));
        }
    }, [dispatch, isRoomTaken]);

    useEffect(() => {
        if (isSuccess && isRoomTaken === false && protection) {
            if (!presenterCode && !protectionTypes?.length) {
                dispatch(setRoomInfoModalAction(true, 'lobby.modals.roomPrivate', 'lobby.modals.roomPrivateDesc'));
            } else if (!presenterCode && !protection.isRoomActive) {
                dispatch(setRoomInfoModalAction(true, 'lobby.modals.roomNotActive', 'lobby.modals.roomNotActiveDesc'));
            }
            dispatch(setRoomProtectionAction(protection));

            const externalAuthProtections = [ProtectionType.EMEDIKUS, ProtectionType.AAI_EDU, ProtectionType.ZOOM];
            if (loggedInUser && protectionTypes && hasCommonElements(protectionTypes, externalAuthProtections)) {
                dispatch(findRoomByIdAction(roomId || '', undefined, params, undefined, presenterCode || undefined));
            }
        }
    }, [dispatch, isRoomTaken, isSuccess, params, presenterCode, protection, protectionTypes, room.presentationId, roomId, loggedInUser]);

    return (
        <>
            {isRoomTaken === false && !infoModal.open && children}
            {isRoomTaken === false && !!presenterCode && <AdminCredentialsModal />}
            {isRoomTaken === false && !presenterCode && protection?.isRoomActive && !!protectionTypes?.length && <UserCredentialsModal />}
            {infoModal.open && <RoomInfoModal />}
        </>
    );
}

export default RoomLoaderWrapper;
